import posthog from 'posthog-js';
import {
  createLoginBinding,
  isRenewalLeasedByAnotherTab,
  isRenewalLooping,
  parseFederatedSessionToken,
  readFederatedTokenFromFragment,
  recordRenewal,
  storeFederatedSession,
  takeLoginBinding,
} from './federated-session';

export const FEDERATED_REFUSAL_PATH = '/auth/refused';
export const FEDERATED_ENTRY_PATH = '/auth/federated';

const refusalUrl = (reason: string) =>
  `${FEDERATED_REFUSAL_PATH}?reason=${encodeURIComponent(reason)}`;

/** Sends the browser to the gateway login, bound to this tab. Always a fresh login. */
export const startFederatedLogin = ({
  startUrl,
  target,
  loginHint,
}: {
  readonly startUrl: string;
  readonly target: string;
  /** Pre-fills the provider login; the test provider picks its fixture from it. */
  readonly loginHint?: string;
}): void => {
  const binding = createLoginBinding();
  if (!binding) {
    window.location.replace(refusalUrl('storage_unavailable'));
    return;
  }
  const url = new URL(startUrl);
  url.search = new URLSearchParams({
    target,
    binding,
    ...(loginHint ? { login_hint: loginHint } : {}),
  }).toString();
  window.location.assign(url.toString());
};

/**
 * Consumes `#federatedSessionToken=…` and hard-redirects, returning true when the caller must
 * skip its bootstrap. Call it first in `main.tsx`, above `initDash0`: the browser's navigation
 * timing entry already holds the fragment, and only a redirect keeps it out of telemetry.
 */
export const consumeFederatedSessionFragment = (): boolean => {
  const token = readFederatedTokenFromFragment(window.location.hash);
  if (!token) return false;

  const binding = takeLoginBinding();
  const session = parseFederatedSessionToken(token);
  // A token this tab did not ask for (a link someone sent) never replaces the current session.
  const accepted =
    session !== null &&
    binding !== null &&
    session.binding === binding &&
    storeFederatedSession(token);

  try {
    posthog.reset();
  } catch {
    // PostHog is not initialised in every app.
  }

  window.location.replace(
    accepted ? `${window.location.pathname}${window.location.search}` : refusalUrl('invalid_flow'),
  );
  return true;
};

/**
 * What a federated request does on a 401 its token can't explain: log in again through the
 * provider (single sign-on, no screen), wait for another tab doing it, or stop a loop.
 */
export const renewFederatedSession = ({
  now = Date.now(),
}: {
  readonly now?: number;
}): 'waiting' | 'renewing' | 'looping' => {
  if (isRenewalLeasedByAnotherTab(now)) return 'waiting';
  if (isRenewalLooping(now)) {
    window.location.replace(refusalUrl('renewal_failed'));
    return 'looping';
  }
  recordRenewal(now);
  window.location.assign(
    `${FEDERATED_ENTRY_PATH}?${new URLSearchParams({
      target: `${window.location.pathname}${window.location.search}`,
    })}`,
  );
  return 'renewing';
};
